import { useState, useCallback } from 'react'
import { uploadAsync, FileSystemUploadType } from 'expo-file-system/legacy'
import { isWeb } from 'tamagui'
import { client } from '@services/api/client'
import { useSession } from '@hooks/useSession'
import { useThemeParams } from '@hooks/useThemeParams'

interface IPresignResponse {
  url: string
  cdn_url: string
}

// Binários publicados antes do expo-image-picker entrar no app recebem este
// JS via OTA sem ter o módulo nativo — o import precisa ser lazy e o erro
// diferenciável para a UI pedir atualização do app em vez de crashar.
export class ImagePickerUnavailableError extends Error {
  constructor() {
    super('expo-image-picker native module unavailable in this binary')
    this.name = 'ImagePickerUnavailableError'
  }
}

/**
 * Avatar upload via the existing core presigned S3 flow:
 * 1. pick image (expo-image-picker, importado lazy — ver
 *    ImagePickerUnavailableError)
 * 2. GET /s3/presignedurl (Bearer) -> { url, cdn_url }
 * 3. PUT the file bytes to the presigned url
 * 4. caller persists cdn_url through userUpdate(coverImageUrl)
 */
export const useAvatarUpload = () => {
  const { user } = useSession()
  const { data: params } = useThemeParams()
  const [uploading, setUploading] = useState(false)

  const pickAndUpload = useCallback(async (): Promise<string | null> => {
    let ImagePicker: typeof import('expo-image-picker')
    try {
      // eslint-disable-next-line @typescript-eslint/no-var-requires
      ImagePicker = require('expo-image-picker')
    } catch (error) {
      // No web não existe loja/binário: falha de require ali é outro problema,
      // então propaga para o tratamento genérico do caller.
      if (isWeb) throw error
      throw new ImagePickerUnavailableError()
    }

    // launchImageLibraryAsync usa o Photo Picker do sistema (Android) e o
    // PHPicker (iOS) — nenhum deles exige permissão de mídia. Não pedir
    // permissão aqui: sem READ_MEDIA_IMAGES no manifest o request retornaria
    // denied e travaria o fluxo (política do Play de fotos/vídeos).
    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ['images'],
      allowsEditing: true,
      aspect: [1, 1],
      quality: 0.8,
    })

    if (result.canceled || !result.assets?.length) return null

    const asset = result.assets[0]
    const schoolId = params?.school?.id
    if (!user?.id || !schoolId) return null

    setUploading(true)
    try {
      const filename = asset.fileName || `avatar-${user.id}.jpg`
      const mimeType = asset.mimeType || 'image/jpeg'

      // Presign único nas duas plataformas: a rota v1, autenticada pelo Bearer
      // que o client já carrega. O web usava /files/upload no origin da escola
      // (Tms::S3Controller, sessão devise por cookie same-origin) — as duas
      // rotas caem no mesmo concern S3Upload do core e respondem o mesmo shape
      // { url, cdn_url }, então sair da rota TMS não muda contrato.
      const { data } = await client.get<IPresignResponse>('/s3/presignedurl', {
        params: {
          type: 'Student',
          id: user.id,
          school_id: schoolId,
          filename,
          kind: 'image',
        },
      })

      if (!data?.url || !data?.cdn_url) {
        throw new Error('Falha ao obter URL de upload (presign vazio)')
      }

      if (isWeb) {
        // Web: o browser resolve o blob da URI local sem problemas.
        const fileResponse = await fetch(asset.uri)
        const blob = await fileResponse.blob()

        const uploadResponse = await fetch(data.url, {
          method: 'PUT',
          body: blob,
          headers: { 'Content-Type': mimeType },
        })

        if (!uploadResponse.ok) {
          throw new Error(
            `Falha no upload da imagem (${uploadResponse.status})`,
          )
        }
      } else {
        // Nativo: fetch(file://).blob() lança "Network request failed". Envia
        // os bytes direto do arquivo com expo-file-system (PUT binário).
        const uploadResponse = await uploadAsync(data.url, asset.uri, {
          httpMethod: 'PUT',
          uploadType: FileSystemUploadType.BINARY_CONTENT,
          headers: { 'Content-Type': mimeType },
        })

        if (uploadResponse.status < 200 || uploadResponse.status >= 300) {
          throw new Error(
            `Falha no upload da imagem (${uploadResponse.status})`,
          )
        }
      }

      return data.cdn_url
    } finally {
      setUploading(false)
    }
  }, [user?.id, params?.school?.id])

  return { pickAndUpload, uploading }
}
